import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, delay, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  Coupon,
  CouponSearchParams,
  CouponStatus,
  COUPON_STATUS,
} from '../coupon.config';
import { CouponManagementService } from '@app/api/promotion-service/api/couponManagement.service';
import { CouponsService } from '@app/api/promotion-service/api/coupons.service';
import { CouponValidateRequest } from '@app/api/promotion-service/model/couponValidateRequest';
import { CouponValidateResponse } from '@app/api/promotion-service/model/couponValidateResponse';
import { CouponFilterDTO } from '@app/api/promotion-service/model/couponFilterDTO';
import { CreateCouponDTO } from '@app/api/promotion-service/model/createCouponDTO';
import { CouponDTO } from '@app/api/promotion-service/model/couponDTO';
import { CouponValidationRequestDTO } from '@app/api/promotion-service/model/couponValidationRequestDTO';
import { CouponValidationResponseDTO } from '@app/api/promotion-service/model/couponValidationResponseDTO';
// import { PageInfoCouponResponseDTO } from '@app/api/promotion-service/model/pageInfoCouponResponseDTO';

// Status constants are now imported from coupon.config.ts

@Injectable({
  providedIn: 'root',
})
export class CouponService {
  private mockCoupons: Coupon[] = [
    {
      id: 1,
      couponCode: 'SUMMER20',
      couponName: 'Summer Sale 20% Off',
      description: 'Summer promotion discount for all customers',
      couponType: 'SEASONAL_EVENT' as any,
      discountType: 'PERCENTAGE' as any,
      discountValue: 20,
      minOrderAmount: 1000000,
      maxDiscountAmount: 200000,
      startDate: '2024-06-01',
      endDate: '2024-08-31',
      usageLimit: 100,
      perCustomerLimit: 1,
      channelRestriction: undefined,
      paymentMethodRestriction: undefined,
      membershipLevel: undefined,
      eventType: undefined,
      deleteFlag: false,
      status: COUPON_STATUS.ACTIVE,
      nextPossibleStatus: ['INACTIVE', 'EXPIRED'],
    },
    {
      id: 2,
      couponCode: 'FIXED50K',
      couponName: 'Fixed Discount 50K',
      description: 'Fixed amount discount for all customers',
      couponType: 'ORDER_WIDE' as any,
      discountType: 'FIXED_AMOUNT' as any,
      discountValue: 50000,
      minOrderAmount: 500000,
      maxDiscountAmount: 50000,
      startDate: '2024-06-01',
      endDate: '2024-12-31',
      usageLimit: 50,
      perCustomerLimit: 1,
      channelRestriction: undefined,
      paymentMethodRestriction: undefined,
      membershipLevel: undefined,
      eventType: undefined,
      deleteFlag: false,
      status: COUPON_STATUS.ACTIVE,
      nextPossibleStatus: ['INACTIVE', 'EXPIRED'],
    },
    {
      id: 3,
      couponCode: 'FIRST100K',
      couponName: 'First Order 100K Off',
      description: 'Special discount for first-time customers',
      couponType: 'LOYALTY_REWARD' as any,
      discountType: 'FIXED_AMOUNT' as any,
      discountValue: 100000,
      minOrderAmount: 200000,
      maxDiscountAmount: 100000,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      usageLimit: 200,
      perCustomerLimit: 1,
      channelRestriction: undefined,
      paymentMethodRestriction: undefined,
      membershipLevel: undefined,
      eventType: undefined,
      deleteFlag: true,
      status: COUPON_STATUS.INACTIVE,
      nextPossibleStatus: ['ACTIVE'],
    },
  ];

  constructor(
    private http: HttpClient,
    private couponManagementService: CouponManagementService,
    private couponsService: CouponsService,
  ) {}

  getCoupons(
    params?: CouponSearchParams,
  ): Observable<{ data: Coupon[]; total: number }> {
    // Create CouponFilterDTO with the new API structure
    const filterDTO: CouponFilterDTO = {
      pageRequest: {
        page: params?.pageRequest?.page || 0,
        size: params?.pageRequest?.size || 20,
        sortBy: params?.pageRequest?.sortBy || 'creationDate',
        ascending: params?.pageRequest?.ascending !== false,
      },
      deleteFlag: false, // Only get non-deleted coupons
    };

    // Only add filter parameters if they have values
    if (params?.couponCode?.trim()) {
      filterDTO.couponCode = params.couponCode.trim();
    }

    if (params?.couponName?.trim()) {
      filterDTO.couponName = params.couponName.trim();
    }

    if (params?.couponType) {
      // Handle both array and single value cases
      const couponTypeValue = Array.isArray(params.couponType)
        ? params.couponType[0]
        : params.couponType;
      // Cast to the specific CouponFilterDTO enum type
      filterDTO.couponType = couponTypeValue as CouponFilterDTO.CouponTypeEnum;
    }

    if (params?.discountType) {
      // Handle both array and single value cases
      const discountTypeValue = Array.isArray(params.discountType)
        ? params.discountType[0]
        : params.discountType;
      // Cast to the specific CouponFilterDTO enum type
      filterDTO.discountType =
        discountTypeValue as CouponFilterDTO.DiscountTypeEnum;
    }

    if (params?.startDate) {
      filterDTO.startDate = params.startDate;
    }

    if (params?.endDate) {
      filterDTO.endDate = params.endDate;
    }

    if (params?.creationDate) {
      filterDTO.creationDate = params.creationDate;
    }

    if (params?.status !== undefined) {
      // Handle both array and single value cases
      filterDTO.status = Array.isArray(params.status)
        ? params.status[0]
        : params.status;
    }

    return this.couponManagementService
      .getAllCoupons(filterDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: any) => {
          // Parse the response if it's a string
          let responseData;
          if (typeof response === 'string') {
            try {
              responseData = JSON.parse(response);
            } catch (e) {
              console.error('Error parsing response:', e);
              responseData = { content: [], totalElements: 0 };
            }
          } else {
            responseData = response;
          }

          // Map API response to our Coupon interface
          const contentArray = responseData?.pageContent || [];
          const coupons: Coupon[] = contentArray.map((apiCoupon: any) => ({
            id: apiCoupon.id,
            couponCode: apiCoupon.couponCode || '',
            couponName: apiCoupon.couponName || '',
            description: apiCoupon.description || '',
            couponType: apiCoupon.couponType || '',
            discountType: apiCoupon.discountType || '',
            discountValue: apiCoupon.discountValue || 0,
            minOrderAmount: apiCoupon.minOrderAmount || 0,
            maxDiscountAmount: apiCoupon.maxDiscountAmount || 0,
            startDate: apiCoupon.startDate || '',
            endDate: apiCoupon.endDate || '',
            usageLimit: apiCoupon.usageLimit || 0,
            perCustomerLimit: apiCoupon.perCustomerLimit || 0,
            channelRestriction: apiCoupon.channelRestriction || '',
            paymentMethodRestriction: apiCoupon.paymentMethodRestriction || '',
            membershipLevel: apiCoupon.membershipLevel || '',
            eventType: apiCoupon.eventType || '',
            deleteFlag: apiCoupon.deleteFlag || false,
            status: apiCoupon.status || COUPON_STATUS.INACTIVE,
            nextPossibleStatus: apiCoupon.nextPossibleStatus || [],
            creationDate: apiCoupon.creationDate || '',
            lastUpdateDate: apiCoupon.lastUpdateDate || '',
            createdBy: apiCoupon.createdBy || '',
            lastUpdatedBy: apiCoupon.lastUpdatedBy || '',
            objectVersionNumber: apiCoupon.objectVersionNumber || 0,
          }));

          return {
            data: coupons,
            total: responseData.totalElements || 0,
          };
        }),
        catchError((error) => {
          console.error('Error fetching coupons:', error);
          // Fallback to mock data for now
          return of(this.mockCoupons).pipe(
            delay(500),
            map((coupons) => {
              // Filter out deleted coupons first
              let filteredCoupons = coupons.filter(
                (coupon) => !coupon.deleteFlag,
              );

              if (params) {
                if (params.couponName?.trim()) {
                  filteredCoupons = filteredCoupons.filter((coupon) =>
                    coupon.couponName
                      .toLowerCase()
                      .includes(params.couponName!.toLowerCase()),
                  );
                }
                if (params.couponType) {
                  const couponTypes = Array.isArray(params.couponType)
                    ? params.couponType
                    : [params.couponType];
                  filteredCoupons = filteredCoupons.filter((coupon) =>
                    couponTypes.includes(coupon.couponType as any),
                  );
                }
                if (params.status !== undefined) {
                  const statuses = Array.isArray(params.status)
                    ? params.status
                    : [params.status];
                  filteredCoupons = filteredCoupons.filter((coupon) =>
                    statuses.includes(coupon.status || COUPON_STATUS.INACTIVE),
                  );
                }
              }
              return {
                data: filteredCoupons,
                total: filteredCoupons.length,
              };
            }),
          );
        }),
      );
  }

  getCouponById(id: number): Observable<Coupon | null> {
    // Use the proper API endpoint to get coupon by ID
    return this.couponManagementService
      .getCouponById(id, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response) => {
          // The API returns JSON directly, no need to parse
          return response as unknown as Coupon;
        }),
        catchError((error) => {
          console.error('Error fetching coupon details:', error);
          // Fallback to mock data for development
          const coupon = this.mockCoupons.find((c) => c.id === id);
          return of(coupon || null).pipe(delay(300));
        }),
      );
  }

  createCoupon(coupon: Omit<Coupon, 'id'>): Observable<Coupon> {
    // Map our Coupon interface to CreateCouponDTO format
    const createCouponDTO: CreateCouponDTO = {
      couponCode: coupon.couponCode,
      couponName: coupon.couponName,
      description: coupon.description,
      couponType: coupon.couponType,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minOrderAmount: coupon.minOrderAmount,
      maxDiscountAmount: coupon.maxDiscountAmount,
      startDate: coupon.startDate,
      endDate: coupon.endDate,
      usageLimit: coupon.usageLimit,
      perCustomerLimit: coupon.perCustomerLimit,
      channelRestriction: coupon.channelRestriction,
      paymentMethodRestriction: coupon.paymentMethodRestriction,
      membershipLevel: coupon.membershipLevel,
      eventType: coupon.eventType,
    };

    return this.couponManagementService
      .createCoupon(createCouponDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: any) => {
          // Map API response back to our format
          return {
            id: response.id,
            couponCode: response.couponCode || '',
            couponName: response.couponName || '',
            description: response.description || '',
            couponType: response.couponType || '',
            discountType: response.discountType || '',
            discountValue: response.discountValue || 0,
            minOrderAmount: response.minOrderAmount || 0,
            maxDiscountAmount: response.maxDiscountAmount || 0,
            startDate: response.startDate || '',
            endDate: response.endDate || '',
            usageLimit: response.usageLimit || 0,
            perCustomerLimit: response.perCustomerLimit || 0,
            channelRestriction: response.channelRestriction || '',
            paymentMethodRestriction: response.paymentMethodRestriction || '',
            membershipLevel: response.membershipLevel || '',
            eventType: response.eventType || '',
            deleteFlag: response.deleteFlag || false,
            status: response.status || COUPON_STATUS.INACTIVE,
            creationDate: response.creationDate || '',
            lastUpdateDate: response.lastUpdateDate || '',
            createdBy: response.createdBy || '',
            lastUpdatedBy: response.lastUpdatedBy || '',
            objectVersionNumber: response.objectVersionNumber || 0,
          };
        }),
        catchError((error) => {
          console.error('Error creating coupon:', error);
          // Re-throw the error so the component can handle it
          throw error;
        }),
      );
  }

  updateCoupon(id: number, coupon: Partial<Coupon>): Observable<Coupon> {
    // Map our Coupon interface to CouponDTO format for update (exclude status and isActive)
    const couponDTO: CouponDTO = {
      id: id,
      couponCode: coupon.couponCode || '',
      couponName: coupon.couponName || '',
      description: coupon.description,
      couponType:
        (coupon.couponType as any) || CouponDTO.CouponTypeEnum.FlatDiscount,
      discountType:
        coupon.discountType || CouponDTO.DiscountTypeEnum.Percentage,
      discountValue: coupon.discountValue,
      minOrderAmount: coupon.minOrderAmount,
      maxDiscountAmount: coupon.maxDiscountAmount,
      startDate: coupon.startDate || '',
      endDate: coupon.endDate || '',
      usageLimit: coupon.usageLimit,
      perCustomerLimit: coupon.perCustomerLimit,
      channelRestriction: coupon.channelRestriction,
      paymentMethodRestriction: coupon.paymentMethodRestriction,
      membershipLevel: coupon.membershipLevel,
      eventType: coupon.eventType,
      // Removed: status - this is managed by the backend
    };

    return this.couponManagementService
      .updateCoupon(couponDTO, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: any) => {
          // Map API response back to our format
          return {
            id: response.id,
            couponCode: response.couponCode || '',
            couponName: response.couponName || '',
            description: response.description || '',
            couponType: response.couponType || '',
            discountType: response.discountType || '',
            discountValue: response.discountValue || 0,
            minOrderAmount: response.minOrderAmount || 0,
            maxDiscountAmount: response.maxDiscountAmount || 0,
            startDate: response.startDate || '',
            endDate: response.endDate || '',
            usageLimit: response.usageLimit || 0,
            perCustomerLimit: response.perCustomerLimit || 0,
            channelRestriction: response.channelRestriction || '',
            paymentMethodRestriction: response.paymentMethodRestriction || '',
            membershipLevel: response.membershipLevel || '',
            eventType: response.eventType || '',
            deleteFlag: response.deleteFlag || false,
            status: response.status || COUPON_STATUS.INACTIVE,
            creationDate: response.creationDate || '',
            lastUpdateDate: response.lastUpdateDate || '',
            createdBy: response.createdBy || '',
            lastUpdatedBy: response.lastUpdatedBy || '',
            objectVersionNumber: response.objectVersionNumber || 0,
          };
        }),
        catchError((error) => {
          console.error('Error updating coupon:', error);
          // Fallback to mock data for now
          const index = this.mockCoupons.findIndex((c) => c.id === id);
          if (index !== -1) {
            this.mockCoupons[index] = {
              ...this.mockCoupons[index],
              ...coupon,
              lastUpdateDate: new Date()
                .toISOString()
                .replace('T', ' ')
                .substring(0, 19),
              lastUpdatedBy: 'current_user',
            };
            return of(this.mockCoupons[index]).pipe(delay(500));
          }
          throw error;
        }),
      );
  }

  deleteCoupon(coupon: Coupon): Observable<boolean> {
    if (!coupon.id) {
      return of(false).pipe(delay(300));
    }

    return this.couponManagementService
      .deleteCoupon(coupon.id, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          // Re-throw the error so it goes to the error handler in the component
          // Component will handle logging and user notification
          return throwError(() => error);
        }),
      );
  }

  toggleCouponStatus(id: number, status: boolean): Observable<boolean> {
    return this.couponManagementService.operateCoupon(id, status).pipe(
      map(() => true),
      catchError((error) => {
        console.error('Error toggling coupon status:', error);
        // Fallback to mock data for now
        const index = this.mockCoupons.findIndex((c) => c.id === id);
        if (index !== -1) {
          this.mockCoupons[index] = {
            ...this.mockCoupons[index],
            status: status ? COUPON_STATUS.ACTIVE : COUPON_STATUS.INACTIVE,
            deleteFlag: !status,
            lastUpdateDate: new Date()
              .toISOString()
              .replace('T', ' ')
              .substring(0, 19),
            lastUpdatedBy: 'current_user',
          };
          return of(true).pipe(delay(300));
        }
        return of(false).pipe(delay(300));
      }),
    );
  }

  updateCouponStatus(id: number, newStatus: string): Observable<boolean> {
    return this.couponManagementService
      .updateCouponStatus(id, newStatus, 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          console.error('Error updating coupon status:', error);
          // Fallback to mock data for now
          const index = this.mockCoupons.findIndex((c) => c.id === id);
          if (index !== -1) {
            this.mockCoupons[index] = {
              ...this.mockCoupons[index],
              status: newStatus as any,
              lastUpdateDate: new Date()
                .toISOString()
                .replace('T', ' ')
                .substring(0, 19),
              lastUpdatedBy: 'current_user',
            };
            return of(true).pipe(delay(300));
          }
          return of(false).pipe(delay(300));
        }),
      );
  }

  validateCoupon(coupon: Coupon): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison

    if (!coupon.couponCode?.trim()) {
      errors.push('Coupon Code is required');
    }

    if (!coupon.couponName?.trim()) {
      errors.push('Coupon Name is required');
    }

    if (!coupon.couponType?.trim()) {
      errors.push('Coupon Type is required');
    }

    // Discount type is auto-populated based on coupon type, so no need to validate it
    // It's hidden from the UI and set programmatically

    if (coupon.discountValue === undefined || coupon.discountValue < 0) {
      errors.push('Discount Value must be non-negative');
    }

    if (coupon.minOrderAmount !== undefined && coupon.minOrderAmount < 0) {
      errors.push('Minimum Order Amount must be non-negative');
    }

    if (
      coupon.maxDiscountAmount !== undefined &&
      coupon.maxDiscountAmount < 0
    ) {
      errors.push('Maximum Discount Amount must be non-negative');
    }

    // Date validation with priority-based error handling
    const startDate = coupon.startDate ? new Date(coupon.startDate) : null;
    const endDate = coupon.endDate ? new Date(coupon.endDate) : null;

    if (startDate) {
      startDate.setHours(0, 0, 0, 0);
    }
    if (endDate) {
      endDate.setHours(0, 0, 0, 0);
    }

    // Priority 1: Required field validation
    if (!coupon.startDate) {
      errors.push('Start Date is required');
    } else if (!coupon.endDate) {
      errors.push('End Date is required');
    }
    // Priority 2: Past date validation (only if both dates are provided)
    else if (startDate && startDate < today) {
      errors.push('common.validation.start_date_past');
    } else if (endDate && endDate < today) {
      errors.push('common.validation.end_date_past');
    }
    // Priority 3: Logical consistency (end date after start date)
    else if (startDate && endDate && endDate < startDate) {
      errors.push('common.validation.end_date_before_start');
    }

    if (coupon.usageLimit !== undefined && coupon.usageLimit < 0) {
      errors.push('Usage Limit must be non-negative');
    }

    if (coupon.perCustomerLimit !== undefined && coupon.perCustomerLimit < 0) {
      errors.push('Per Customer Limit must be non-negative');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Validate issued coupon code (POST /v1/coupons/validate).
   */
  validateIssuedCouponCode(
    couponCode: string,
    channelCode: string,
  ): Observable<CouponValidateResponse> {
    const req: CouponValidateRequest = {
      couponCode: (couponCode || '').trim(),
      channelCode: (channelCode || '').trim() || undefined,
    };
    return this.couponsService.validate(req, 'body', false, {
      httpHeaderAccept: 'application/json' as any,
    });
  }

  /**
   * Search issued coupons (phiếu đã phát hành) by code.
   */
  searchIssuedCoupons(
    couponCode: string,
  ): Observable<
    Array<{
      id?: number;
      couponCode: string;
      couponName?: string;
    }>
  > {
    const code = (couponCode || '').trim();
    if (!code) {
      return of([]);
    }

    return this.couponsService
      .getCouponsPage(
        {
          page: 0,
          size: 20,
          sortBy: 'creationDate',
          ascending: false,
          couponCode: code,
          status: 'ACTIVE',
        },
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(
        map((response) => {
          const rows = response?.pageContent ?? [];
          return rows
            .filter((row) => (row.couponCode || '').trim())
            .map((row) => ({
              id: row.id,
              couponCode: (row.couponCode || '').trim(),
              couponName: (row.couponCode || '').trim(),
            }));
        }),
        catchError((error) => {
          console.error('Error searching issued coupons:', error);
          return of([]);
        }),
      );
  }

  isCouponValidationSuccessful(
    response: CouponValidationResponseDTO & { valid?: boolean },
  ): boolean {
    return response?.isValid === true || response?.valid === true;
  }

  /**
   * Map validate response to coupon chip used in O2O checkout.
   */
  toAppliedCouponFromValidation(
    response: CouponValidationResponseDTO & { valid?: boolean },
    fallback?: {
      couponCode?: string;
      couponName?: string;
      discountType?: string;
      discountValue?: number;
      minOrderAmount?: number;
      maxDiscountAmount?: number;
    },
  ): {
    couponCode: string;
    couponName?: string;
    discountType?: string;
    discountValue?: number;
    minOrderAmount?: number;
    maxDiscountAmount?: number;
    discountAmount?: number;
  } | null {
    if (!this.isCouponValidationSuccessful(response)) {
      return null;
    }

    const applied = response.appliedCoupon;
    const code = (
      applied?.couponCode ||
      fallback?.couponCode ||
      ''
    ).trim();
    if (!code) {
      return null;
    }

    return {
      couponCode: code,
      couponName: applied?.couponName ?? fallback?.couponName,
      discountType: applied?.discountType ?? fallback?.discountType,
      discountValue: applied?.discountValue ?? fallback?.discountValue,
      minOrderAmount: fallback?.minOrderAmount,
      maxDiscountAmount: fallback?.maxDiscountAmount,
      discountAmount: response.discountAmount ?? applied?.discountAmount,
    };
  }

  /**
   * Validate coupon with backend using full order/customer context
   */
  validateCouponWithContext(
    request: CouponValidationRequestDTO,
  ): Observable<CouponValidationResponseDTO> {
    return this.couponManagementService.validateCoupon(request, 'body', false, {
      httpHeaderAccept: 'application/json' as any,
    });
  }
}
